import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';

import { environment } from '@ib-env/environment';
import { AbstractBaseComponent } from '@ib-shared/abstract/abstract-base';
import { ISelectOption } from '@ib-shared/interfaces/select-option.intf';
import { CountriesHttpService } from '@ib-shared/services/countries-data.service';
import {
  INVESTOR_ACCOUNT_TYPE,
  INVESTOR_SAVING_FOR,
  INVESTOR_WHAT_FOR,
} from 'credebt-shared';
import * as _ from 'lodash';
import { filter, map, Observable } from 'rxjs';

import { SIGN_UP_FIELDS } from '../../../enums/sign-up-fields.enum';
import { SignUpFacadeService } from '../../../services/sign-up-facade.service';
import { SignUpFormHelperService } from '../../../services/sign-up-form-facade.service';

interface IQuestion {
  title: string;
  type: SIGN_UP_FIELDS;
  options?: Array<ISelectOption>;
}
@Component({
  selector: 'ib-sign-up-step-objective',
  templateUrl: './sign-up-step-objective.component.html',
  styleUrls: ['./sign-up-step-objective.component.scss'],
})
export class SignUpStepObjectiveComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy
{
  @Input() form: FormGroup;

  public readonly FORM_FIELDS = SIGN_UP_FIELDS;
  public readonly INVESTOR_SAVING_FOR = INVESTOR_SAVING_FOR;
  public readonly INVESTOR_WHAT_FOR = INVESTOR_WHAT_FOR;
  public questions: Array<IQuestion>;
  public userTypes = [
    { label: 'Personal Account', value: INVESTOR_ACCOUNT_TYPE.PERSONAL },
    { label: 'Company Account', value: INVESTOR_ACCOUNT_TYPE.COMPANY },
  ];
  public ACC_TYPE = INVESTOR_ACCOUNT_TYPE;
  public readonly SIGN_UP_FIELDS = SIGN_UP_FIELDS;
  public currencies$: Observable<Array<ISelectOption>>;
  savingFor: IQuestion = this.getSavingFor();
  savingForCompany: IQuestion = this.getSavingForCompany();
  whatFor: IQuestion = this.getWhatFor();
  howMuch: IQuestion = this.getHowMuch();

  @ViewChild('otherInput', { static: false })
  otherInput: ElementRef<HTMLInputElement>;
  @ViewChild('savingForOtherInput', { static: false })
  savingForOtherInput: ElementRef<HTMLInputElement>;

  constructor(
    public facadeS: SignUpFacadeService,
    public formS: SignUpFormHelperService,
    private countriesHttpS: CountriesHttpService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.initCurrencies();
    this.initForm();
  }

  ngOnDestroy(): void {
    super.destroySubscriptions();
  }

  initCurrencies(): void {
    this.currencies$ = this.countriesHttpS.selectAllCountries().pipe(
      filter(r => Boolean(r.length)),
      map(r => {
        const list = _.uniqBy(r, 'currency').map(i => ({
          label: i.currency,
          value: i.currency,
        }));
        return list;
      }),
    );
  }

  private initForm(): void {
    // mocks
    if (!environment.production && environment.allowSignUpUseMocks) {
      this.form
        .get(SIGN_UP_FIELDS.ACCOUNT_TYPE)
        .setValue(INVESTOR_ACCOUNT_TYPE.PERSONAL);
      this.form
        .get(SIGN_UP_FIELDS.SAVING_FOR)
        .setValue(INVESTOR_SAVING_FOR.MONTH);
      this.form.get(SIGN_UP_FIELDS.WHAT_FOR).setValue(INVESTOR_WHAT_FOR.HOUSE);
      this.form.get(SIGN_UP_FIELDS.HOW_MUCH).setValue(3333);
      this.form.get(SIGN_UP_FIELDS.HOW_MUCH_CURRENCY).setValue('eur');
    }
  }

  private getSavingFor(): IQuestion {
    return {
      title: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.q`,
      type: SIGN_UP_FIELDS.SAVING_FOR,
      options: [
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.MONTH}`,
          value: INVESTOR_SAVING_FOR.MONTH,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.QUARTER}`,
          value: INVESTOR_SAVING_FOR.QUARTER,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.OCCASSIONAL}`,
          value: INVESTOR_SAVING_FOR.OCCASSIONAL,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.LUMP_SUM_INVEST}`,
          value: INVESTOR_SAVING_FOR.LUMP_SUM_INVEST,
        },
      ],
    };
  }

  private getSavingForCompany(): IQuestion {
    return {
      title: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.q-company`,
      type: SIGN_UP_FIELDS.SAVING_FOR,
      options: [
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.SHORT_TERM_DEPOSIT_WITHDRAW}`,
          value: INVESTOR_SAVING_FOR.SHORT_TERM_DEPOSIT_WITHDRAW,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.SHORT_TERM_VAT3_CT1}`,
          value: INVESTOR_SAVING_FOR.SHORT_TERM_VAT3_CT1,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.REGULAR_SAVING_UNUSED_CASH}`,
          value: INVESTOR_SAVING_FOR.REGULAR_SAVING_UNUSED_CASH,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.LONG_TERM_SAVINGS}`,
          value: INVESTOR_SAVING_FOR.LONG_TERM_SAVINGS,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.SAVING_FOR}.options.${INVESTOR_SAVING_FOR.OTHER}`,
          value: INVESTOR_SAVING_FOR.OTHER,
        },
      ],
    };
  }

  checkFocus(): void {
    setTimeout(() => {
      if (this.formS.whatForFormCtrl.value === INVESTOR_WHAT_FOR.OTHER) {
        this.otherInput?.nativeElement?.focus();
      }
      if (this.formS.savingForFormCtrl.value === INVESTOR_WHAT_FOR.OTHER) {
        this.savingForOtherInput?.nativeElement?.focus();
      }
    }, 100);
  }

  private getWhatFor(): IQuestion {
    return {
      title: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.q`,
      type: SIGN_UP_FIELDS.WHAT_FOR,
      options: [
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.options.${INVESTOR_WHAT_FOR.HOUSE}`,
          value: INVESTOR_WHAT_FOR.HOUSE,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.options.${INVESTOR_WHAT_FOR.UNEXPECTED}`,
          value: INVESTOR_WHAT_FOR.UNEXPECTED,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.options.${INVESTOR_WHAT_FOR.RAINY_DAY}`,
          value: INVESTOR_WHAT_FOR.RAINY_DAY,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.options.${INVESTOR_WHAT_FOR.TRAVEL}`,
          value: INVESTOR_WHAT_FOR.TRAVEL,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.options.${INVESTOR_WHAT_FOR.GOALS}`,
          value: INVESTOR_WHAT_FOR.GOALS,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.options.${INVESTOR_WHAT_FOR.RETIREMENT}`,
          value: INVESTOR_WHAT_FOR.RETIREMENT,
        },
        {
          label: `page.sign-up.questions.${SIGN_UP_FIELDS.WHAT_FOR}.options.${INVESTOR_WHAT_FOR.OTHER}`,
          value: INVESTOR_WHAT_FOR.OTHER,
        },
      ],
    };
  }

  private getHowMuch(): IQuestion {
    return {
      title: `page.sign-up.questions.${SIGN_UP_FIELDS.HOW_MUCH}.q`,
      type: SIGN_UP_FIELDS.HOW_MUCH,
    };
  }

  next(): void {
    this.facadeS.setNextStep();
  }
}
